import '.a{display:grid;}.ssr-plugin'
import '.gf{grid-template-columns:repeat(3, minmax(250px, 1fr));}.ssr-plugin'
import '.gg{column-gap:1.5rem;}.ssr-plugin'
import '.fq{row-gap:2rem;}.ssr-plugin'
import { useTranslations } from '@i18n/useLanguage'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useClient, useLoading, useQuery } from '@based/sdk/react'
import defaultProjectPicture from '@app/assets/defaultProjectPicture.png'
import { InitiativeSearchGrid } from '../sections/InitiativeSearchGrid'
import { match } from 'ts-pattern'
import { SearchMap } from '../sections/SearchMap'
import { useLocation, useSearchParams } from 'wouter'
import { pages } from '@app/util/links'
import { InitiativeFilter } from 'functions/app/initiative/list/filters'
import { ListInitiativeModel } from 'functions/app/initiative/list'
import { InitiativeFilterDrawer } from '../sections/InitiativeFilterDrawer'
import {
  useQueryStates,
  parseAsArrayOf,
  parseAsInteger,
  parseAsBoolean,
  parseAsString,
} from 'nuqs'
import { ListPhaseModel } from 'functions/app/dashboard/supporting-entities/phase/list'
import type { InitiativeSpotlightModel } from 'functions/app/spotlight/list-initiatives'
import { SectionHeader } from '@app/components/blocks/sectionHeader/SectionHeader'
import { InitiativeCard } from '@app/components/blocks/card/InitiativeCard'
import { labelAddressBuilder } from '@app/util/helpers'
import { styled } from 'inlines'
import useMediaQuery from '@app/util/useMediaQuery'
import Column from '@app/components/layout/Column'

export const DiscoverInitiatives = ({
  view,
  searchTerm,
  onResetSearch,
  filtersOpen,
  onFiltersClose,
}: {
  view: 'grid' | 'map'
  searchTerm: string
  onResetSearch: () => void
  filtersOpen: boolean
  onFiltersClose: () => void
}) => {
  const PAGE_SIZE = 15
  const t = useTranslations()
  const translations = t.app.discoveryPage.tabs.initiatives
  const commonTranslations = t.app.common
  const [total, setTotal] = useState(0)
  const [initiatives, setInitiatives] = useState<ListInitiativeModel[]>([])
  const [top, setTop] = useState(PAGE_SIZE)
  const [mounted, setMounted] = useState(false)
  const client = useClient()
  const isLoading = useLoading()
  const [, navigate] = useLocation()
  const [searchParams] = useSearchParams()
  const isSearchActive = searchTerm.length > 0
  const [filterParams, setFilterParams] = useQueryStates({
    phase: parseAsArrayOf(parseAsInteger),
    developmentForm: parseAsArrayOf(parseAsInteger),
    searchingForMembers: parseAsBoolean,
    applyPhase: parseAsString,
  })
  const { data: phases } = useQuery('list-phase')
  const { data: spotlightInitiatives } = useQuery(
    'list-spotlight-initiatives',
    {
      searchTerm: searchTerm,
      filters: filterParams,
    },
  ) as { data?: InitiativeSpotlightModel[] }

  const initiativesToExclude: number[] = useMemo(() => {
    return isSearchActive
      ? []
      : (spotlightInitiatives ?? []).map((s) => s.initiative.id)
  }, [spotlightInitiatives, isSearchActive])

  const getApplyPhase = searchParams.get('applyPhase')
  useEffect(() => {
    const phaseType = filterParams.applyPhase ?? getApplyPhase
    if (!phaseType || !phases?.length) {
      return
    }
    const phase = phases.find((p: ListPhaseModel) => p.phaseType === phaseType)
    if (phase) {
      void setFilterParams({
        phase: [phase.id],
        applyPhase: null,
      })
    } else {
      void setFilterParams({ applyPhase: null })
    }
  }, [filterParams.applyPhase, getApplyPhase, phases, setFilterParams])

  // Wrapper to match InitiativeFilterDrawer's expected setFilters signature
  const setFilters = useCallback(
    (newFilters: InitiativeFilter) => {
      void setFilterParams({
        phase: newFilters.phase ?? null,
        developmentForm: newFilters.developmentForm ?? null,
        searchingForMembers: newFilters.searchingForMembers ?? null,
      })
    },
    [setFilterParams],
  )

  // useQuery is for SSR and client side queries will be managed by client.query
  const dataServerQuery = useQuery('list-initiative', {
    searchTerm: searchTerm,
    top: PAGE_SIZE,
    desc: true,
    initiativesToExclude,
  })

  const serverInitiatives: ListInitiativeModel[] = dataServerQuery.data ?? []

  const query = useCallback(
    (topParam: number) => {
      const close = client
        .query('list-initiative', {
          searchTerm: searchTerm,
          top: topParam,
          filters: filterParams,
          desc: true,
          initiativesToExclude,
        })
        .subscribe((result) => {
          setInitiatives(result)
          setMounted(true)
        })
      return () => close()
    },
    [client, searchTerm, filterParams, initiativesToExclude],
  )
  const countQuery = useCallback(() => {
    const close = client
      .query('list-initiative', {
        searchTerm: searchTerm,
        filters: filterParams,
        count: true,
        initiativesToExclude,
      })
      .subscribe((r) => setTotal(r.count))
    return () => close()
  }, [client, searchTerm, filterParams, initiativesToExclude])

  useEffect(() => {
    setTop(PAGE_SIZE)
    const cleanupCount = countQuery()
    const cleanupQuery = query(PAGE_SIZE)
    return () => {
      cleanupCount()
      cleanupQuery()
    }
  }, [searchTerm, filterParams, countQuery, query])

  useEffect(() => {
    if (top === PAGE_SIZE) {
      return
    }
    const cleanup = query(top)
    return () => cleanup()
  }, [top, query])

  const renderedInitiatives = mounted ? initiatives : serverInitiatives
  const currentTotal = mounted ? total : serverInitiatives.length
  const isMobile = useMediaQuery()
  const hasSpotlights = !isSearchActive && spotlightInitiatives?.length > 0

  return (
    <>
      <InitiativeFilterDrawer
        open={filtersOpen}
        onClose={onFiltersClose}
        filters={filterParams}
        setFilters={setFilters}
      />

      {hasSpotlights && (
        <Column alignItems="stretch">
          <SectionHeader
            title={translations.spotlight.title}
            subtitle={translations.spotlight.subTitle}
          />
          <styled.div className="a gf gg fq"
            style={{
              '@media (max-width: 1200px)': {
                gridTemplateColumns: 'repeat(2, minmax(250px, 1fr))',
              },
              '@media (max-width: 800px)': {
                gridTemplateColumns: 'repeat(1, minmax(250px, 1fr))',
              },}}
          >
            {(spotlightInitiatives ?? []).map((spotlight, index) => {
              const initiative = spotlight.initiative
              const isSpotlight = index === 0 && !isMobile
              return (
                <InitiativeCard
                  key={initiative.slug}
                  variant={isSpotlight ? 'spotlight' : 'default'}
                  style={{
                    gridColumn: isSpotlight ? '1 / span 2' : null,
                  }}
                  to={pages.initiative(initiative.slug)}
                  image={initiative.images?.[0]?.src ?? defaultProjectPicture}
                  title={initiative.title}
                  description={isSpotlight ? initiative.description : null}
                  location={labelAddressBuilder(
                    initiative.location.address,
                    commonTranslations.notSpecified,
                  )}
                  phase={initiative.phase?.name}
                  tagText={
                    initiative.searchingForMembers
                      ? commonTranslations.initiativeStatus.searchingForMembers
                      : commonTranslations.initiativeStatus
                          .notSearchingForMembers
                  }
                  tagVariant={initiative.searchingForMembers ? 'green' : 'gray'}
                />
              )
            })}
          </styled.div>
        </Column>
      )}

      {match(view)
        .with('grid', () => (
          <InitiativeSearchGrid
            items={renderedInitiatives.map((initiative) => ({
              id: initiative.slug,
              to: pages.initiative(initiative.slug),
              image: initiative.images?.[0]?.src ?? defaultProjectPicture,
              title: initiative.title,
              description: initiative.description,
              address: initiative.location.address,
              phase: initiative.phase?.name,
              tagText: initiative.searchingForMembers
                ? commonTranslations.initiativeStatus.searchingForMembers
                : commonTranslations.initiativeStatus.notSearchingForMembers,
              tagVariant: initiative.searchingForMembers ? 'green' : 'gray',
            }))}
            totalItems={currentTotal}
            title={translations.title}
            subtitle={translations.subTitle}
            isLoading={isLoading}
            searchTerm={searchTerm}
            loadMore={() => setTop((prev) => prev + PAGE_SIZE)}
            onResetSearch={onResetSearch}
            onNavigate={(key) => navigate(pages.initiative(key))}
          />
        ))
        .with('map', () => (
          <SearchMap
            items={initiatives.map((initiative) => ({
              id: initiative.uuid,
              image: initiative.images?.[0]?.src ?? defaultProjectPicture,
              title: initiative.title,
              description: initiative.description,
              address: initiative.location.address,
              status: initiative.phase.name,
              lat: initiative.location.geo.lat,
              long: initiative.location.geo.long,
            }))}
            totalItems={currentTotal}
            title={translations.title}
            subtitle={translations.subTitle}
            isLoading={isLoading}
            moreToLoad={true}
            searchTerm={searchTerm}
            loadMore={() => setTop((prev) => prev + PAGE_SIZE)}
            onResetSearch={onResetSearch}
          />
        ))
        .exhaustive()}
    </>
  )
}
